/**
 * Task Contexts: URLs, labels and where-am-I parsing, shared by the worker and the frontend.
 */
import {
  isTaskContextType,
  type TaskContextRef,
  type TaskContextType,
} from './types/tasks';

export const TASK_CONTEXT_TYPE_LABELS: Record<TaskContextType, string> = {
  product_batch: 'New product batch',
  product_batch_product: 'Batch product',
  product: 'Product',
  variant: 'Variant',
  vendor: 'Brand',
  supplier: 'Supplier',
  purchase_order: 'Purchase order',
  collection: 'Collection',
  conversation: 'Email',
  page: 'Page',
  url: 'Web page',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidLike(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Where a Context lives in jflo. `parentId` is the batch for a batch product;
 * `poNumber` finds a purchase order in the list (it has no page of its own).
 */
export function taskContextUrl(
  ref: TaskContextRef,
  extra: { parentId?: string | null; poNumber?: string | null } = {}
): string {
  const id = encodeURIComponent(ref.id);
  switch (ref.type) {
    case 'product_batch':
      return `/product-batches/${id}`;
    case 'product_batch_product':
      return extra.parentId
        ? `/product-batches/${encodeURIComponent(extra.parentId)}?product=${id}`
        : '/product-batches';
    case 'product':
      return `/product-details/${id}`;
    case 'variant':
      return `/variant-details/${id}`;
    case 'vendor':
      return `/vendors/${id}`;
    case 'supplier':
      return `/suppliers/${id}`;
    case 'purchase_order':
      return extra.poNumber
        ? // `statuses=` (every status) makes the link set the whole view, so the list's remembered
          // filters can't hide the PO (url-and-saved-state.mdc, last view).
          `/planning/purchase-orders?q=${encodeURIComponent(extra.poNumber)}&statuses=`
        : '/planning/purchase-orders';
    case 'collection':
      return `/collections?collection=${id}`;
    case 'conversation':
      return `/inbox/${id}`;
    case 'page':
      return ref.id.startsWith('/') ? ref.id : `/${ref.id}`;
    case 'url':
      return ref.id;
  }
}

/** Context types whose id is text rather than a uuid, with no row behind them. */
export function isAddressContextType(type: TaskContextType): type is 'page' | 'url' {
  return type === 'page' || type === 'url';
}

/**
 * A web page outside jflo as a `url` Context id: http(s) only, without the #fragment. Null when it
 * isn't a web address. Tasks on a page are found by the address without its query (as `page` does).
 */
export function webPageContextId(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  url.hash = '';
  const id = url.toString();
  return id.length <= 1000 ? id : null;
}

/** What the Tasks panel shows for the page you're on. */
export interface TaskPageScope {
  /** Pre-filled Context for quick-add. */
  primary: TaskContextRef;
  /** Tasks on any of these (or whose parent is one of these) are listed. */
  scope: TaskContextRef[];
}

/** Page contexts store path + query; the panel matches them on the path alone. */
export function pageContextId(pathname: string, search: string): string {
  const query = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
  return `${pathname}${query}`;
}

export function pageContextPath(id: string): string {
  const q = id.indexOf('?');
  return q === -1 ? id : id.slice(0, q);
}

/** Work out the Context(s) of a jflo URL. Pages without a typed Context become a Page context. */
export function taskScopeFromLocation(pathname: string, search: string): TaskPageScope {
  const params = new URLSearchParams(search);
  const segments = pathname.split('/').filter(Boolean);
  const [first, second, third] = segments;

  const typed = (type: TaskContextType, id: string): TaskPageScope => ({
    primary: { type, id },
    scope: [{ type, id }],
  });

  if (first === 'product-batches' && isUuidLike(second) && !third) {
    const batch: TaskContextRef = { type: 'product_batch', id: second };
    const productId = params.get('product');
    if (isUuidLike(productId)) {
      return { primary: { type: 'product_batch_product', id: productId }, scope: [batch] };
    }
    return { primary: batch, scope: [batch] };
  }
  if (first === 'product-details' && isUuidLike(second)) return typed('product', second);
  if (first === 'variant-details' && isUuidLike(second)) return typed('variant', second);
  if (first === 'vendors' && isUuidLike(second)) return typed('vendor', second);
  if (first === 'suppliers' && isUuidLike(second)) return typed('supplier', second);
  if (first === 'inbox' && isUuidLike(second)) return typed('conversation', second);
  if (first === 'collections' && !second) {
    const collectionId = params.get('collection');
    if (isUuidLike(collectionId)) return typed('collection', collectionId);
  }

  return {
    primary: { type: 'page', id: pageContextId(pathname, search) },
    scope: [{ type: 'page', id: pathname }],
  };
}

/** Parse a jflo link (e.g. pasted into Slack) into a Context; null when it isn't a jflo page. */
export function taskContextFromUrl(raw: string, appOrigin: string): TaskContextRef | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  let origin: string;
  try {
    origin = new URL(appOrigin).origin;
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (url.pathname === '/' || url.pathname.startsWith('/tasks')) return null;
  return taskScopeFromLocation(url.pathname, url.search).primary;
}

/* ------------------------------------------------------------------ */
/* Mentions: `@[Label](type:id)` in descriptions and chat.             */
/* ------------------------------------------------------------------ */

export type TaskMentionTarget = { kind: 'user'; id: string } | { kind: 'context'; ref: TaskContextRef } | { kind: 'ai' };

/** The AI builder (the task loop, T-393) as a mention: `@[AI builder](ai:builder)`. */
export const AI_BUILDER_MENTION_TYPE = 'ai';
export const AI_BUILDER_MENTION_ID = 'builder';

export interface TaskMentionToken {
  label: string;
  target: TaskMentionTarget;
  start: number;
  end: number;
}

const MENTION_RE = /@\[([^\]\n]{1,200})\]\(([a-z_]+):([^)\s]{1,500})\)/g;

export function mentionMarkup(label: string, type: 'user' | 'ai' | TaskContextType, id: string): string {
  const clean = label.replace(/[[\]\n]/g, ' ').trim() || type;
  return `@[${clean}](${type}:${id})`;
}

export function parseMentionTokens(text: string): TaskMentionToken[] {
  const tokens: TaskMentionToken[] = [];
  for (const match of text.matchAll(MENTION_RE)) {
    const [whole, label, type, id] = match;
    const start = match.index ?? 0;
    if (type === 'user') {
      if (!isUuidLike(id)) continue;
      tokens.push({ label, target: { kind: 'user', id }, start, end: start + whole.length });
    } else if (type === AI_BUILDER_MENTION_TYPE) {
      if (id !== AI_BUILDER_MENTION_ID) continue;
      tokens.push({ label, target: { kind: 'ai' }, start, end: start + whole.length });
    } else if (isTaskContextType(type) && !isAddressContextType(type)) {
      if (!isUuidLike(id)) continue;
      tokens.push({ label, target: { kind: 'context', ref: { type, id } }, start, end: start + whole.length });
    }
  }
  return tokens;
}

export function parseMentions(text: string): {
  userIds: string[];
  contexts: Array<TaskContextRef & { title: string }>;
  /** The AI builder is @'d: on its own, as it isn't a person (no follower, bell or Slack DM). */
  aiBuilder: boolean;
} {
  const userIds = new Set<string>();
  const contexts = new Map<string, TaskContextRef & { title: string }>();
  let aiBuilder = false;
  for (const token of parseMentionTokens(text)) {
    if (token.target.kind === 'user') userIds.add(token.target.id);
    else if (token.target.kind === 'ai') aiBuilder = true;
    else {
      const { ref } = token.target;
      contexts.set(`${ref.type}:${ref.id}`, { ...ref, title: token.label });
    }
  }
  return { userIds: [...userIds], contexts: [...contexts.values()], aiBuilder };
}

/** Plain text for notifications and Slack: `@[Sam](user:…)` becomes `@Sam`. */
export function mentionsToPlainText(text: string): string {
  return text.replace(MENTION_RE, (_whole, label: string) => `@${label}`);
}

export function taskContextKey(ref: TaskContextRef): string {
  return `${ref.type}:${ref.id}`;
}
